import React, { useMemo, useState } from "react";
import { resendFailedEmailService } from "@/service/email.service";
import { FilterSelectWithRadio } from "@/components/form";
import QuillEditor from "./QuillEditor";

export type Option = { value: string; label: string };

const FieldLabel: React.FC<{ label: string; required?: boolean }> = ({
  label,
  required,
}) => (
  <label className="text-sm text-[#1E1E1E] font-[500]">
    {label}
    {required ? <span className="text-[#FF3B30]">*</span> : null}
  </label>
);

const InfoField: React.FC<{
  label: string;
  value?: string;
  danger?: boolean;
}> = ({ label, value, danger }) => (
  <div className="flex flex-col gap-1 w-full">
    <label className="text-xs text-[#6C6C6C]">{label}</label>
    <div
      className={`h-[38px] px-3 flex items-center border border-gray-200 rounded-[6px] text-sm bg-[#FFFFFF] ${
        danger ? "text-[#DC2626]" : "text-[#1E1E1E]"
      }`}
    >
      {value || "-"}
    </div>
  </div>
);

const quillModules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
  ],
};

export type ResendEmailData = {
  reason?: string | null;
  clientId: string;
  clientName: string;
  projectId: string;
  projectName: string;
  scenario: string;
  lastSendDate?: string | null;
  end_date?: string;
  esti_hrs?: string | number;
  util_hrs?: string | number;
  remi_hrs?: string | number;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  bodyHtml: string;
};

const ResendFailedEmailForm: React.FC<{
  data: ResendEmailData;
  clientOptions: Option[];
  projectOptions: Option[];
  scenarioOptions: Option[];
  emailId: string;
  onCancel: () => void;
  onSubmit?: (payload: any) => void;
}> = ({
  data,
  clientOptions,
  projectOptions,
  scenarioOptions,
  emailId,
  onCancel,
  onSubmit,
}) => {
  const [to, setTo] = useState<string>(data.to?.join(", ") || "");
  const [cc, setCc] = useState<string>(data.cc?.join(", ") || "");
  const [bcc, setBcc] = useState<string>(data.bcc?.join(", ") || "");
  const [submitting, setSubmitting] = useState(false);

  const isRemainingNegative = useMemo(() => {
    const num = Number.parseFloat(String(data.remi_hrs ?? "0").toString());
    return !Number.isNaN(num) && num < 0;
  }, [data.remi_hrs]);

  const canSubmit = useMemo(() => {
    return !!to && !!data.subject && !!data.bodyHtml;
  }, [to, data.subject, data.bodyHtml]);

  return (
    <form
      className="p-6 flex flex-col gap-5"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        try {
          setSubmitting(true);
          const normalizeEndDate = (v?: string | null) => {
            if (!v) return "";
            const onlyDigits = String(v).replace(/\D+/g, "");
            if (/^\d{8}$/.test(onlyDigits)) return onlyDigits;
            const m = /^([0-3]?\d)[/-]([0-1]?\d)[/-](\d{4})$/.exec(String(v));
            if (m) return `${m[1].padStart(2, "0")}${m[2].padStart(2, "0")}${m[3]}`;
            return onlyDigits;
          };

          const toArr = to
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
          const ccArr = cc
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
          const bccArr = bcc
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);

          const payload = {
            project_id: data.projectId,
            client_id: data.clientId,
            scenario: data.scenario,
            esti_hrs: Number(data.esti_hrs ?? 0) || 0,
            util_hrs: Number(data.util_hrs ?? 0) || 0,
            remi_hrs: Number(data.remi_hrs ?? 0) || 0,
            end_date: normalizeEndDate(data.end_date ?? null),
            to: toArr.length ? toArr : data.to ?? [],
            cc: ccArr.length ? ccArr : data.cc ?? [],
            bcc: bccArr.length ? bccArr : data.bcc ?? [],
            subject: data.subject,
            body: data.bodyHtml,
            action: "send_now" as const,
          };

          await resendFailedEmailService(emailId, payload);
          if (onSubmit) onSubmit(payload);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      <div className="text-[16px] font-[500] leading-[20px] text-[#1E1E1E]">
        The email failed to send. Please review and send again.
      </div>

      <div className="flex flex-col gap-1">
        <FieldLabel label="Reason" />
        <input
          type="text"
          readOnly
          value={data.reason || ""}
          className="h-[38px] px-3 border border-gray-200 rounded-[6px] text-sm text-[#1E1E1E] bg-[#F9FAFB] w-full"
        />
      </div>

      <p className="text-sm text-[#6C6C6C]">
        <span className="text-[#FF3B30]">*</span> indicates mandatory fields
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <FieldLabel label="Client name" required />
          <FilterSelectWithRadio
            options={clientOptions}
            width="w-full"
            value={data.clientId}
            bgColor="bg-[#FFFFFF]"
            onChange={() => {}}
          />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel label="Project" required />
          <FilterSelectWithRadio
            options={projectOptions}
            width="w-full"
            value={data.projectId}
            bgColor="bg-[#FFFFFF]"
            onChange={() => {}}
          />
        </div>
      </div>

      {data.lastSendDate ? (
        <div className="text-sm text-[#1E1E1E]">
          Last email was sent on {data.lastSendDate}{" "}
          <button type="button" className="text-[#6093A0] underline">
            View emails
          </button>
        </div>
      ) : null}

      <div className="flex w-[50%] flex-col gap-1">
        <InfoField label="Scenario" value={data.scenario.charAt(0).toUpperCase() + data.scenario.slice(1)} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <InfoField label="End date" value={data.end_date} />
        <InfoField
          label="Estimated hours"
          value={String(data.esti_hrs ?? "-")}
        />
        <InfoField
          label="Utilized hours"
          value={String(data.util_hrs ?? "-")}
          danger={isRemainingNegative}
        />
        <InfoField
          label="Remaining hours"
          value={String(data.remi_hrs ?? "-")}
          danger={isRemainingNegative}
        />
      </div>

      <div className="flex flex-col gap-1">
        <FieldLabel label="To" required />
        <input
          type="text"
          placeholder="Enter email addresses"
          className="h[42px] px-3 border border-gray-200 rounded-[6px] focus:outline-none h-[42px]"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <FieldLabel label="Cc" />
          <input
            type="text"
            placeholder="Enter cc recipients"
            className="h-[42px] px-3 border border-gray-200 rounded-[6px] focus:outline-none"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel label="Bcc" />
          <input
            type="text"
            placeholder="Enter bcc recipients"
            className="h-[42px] px-3 border border-gray-200 rounded-[6px] focus:outline-none"
            value={bcc}
            onChange={(e) => setBcc(e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <FieldLabel label="Subject" required />
        <input
          type="text"
          disabled
          className="h-[42px] px-3 border border-gray-200 rounded-[6px] focus:outline-none"
          value={data.subject}
          // readOnly
        />
      </div>

      <div className="flex flex-col gap-1">
        <FieldLabel label="Email content" required />
        <div>
          <QuillEditor
            value={data.bodyHtml}
            onChange={() => {}}
            modules={quillModules}
            placeholder="Enter email content"
            className="min-h-[160px]"
            // readOnly
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-4 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="h-[36px] px-4 rounded-md bg-[#F5F5F5] text-[#1E1E1E] text-sm hover:bg-gray-100"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSubmit || submitting}
          className="h-[36px] px-4 rounded-md text-white text-sm bg-[#73B5C5] hover:bg-[#5da7b8] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? "Sending..." : "Send again"}
        </button>
      </div>
    </form>
  );
};

export default ResendFailedEmailForm;
